import {
  Building2, Target, Calculator, CalendarCheck, FileDown, Presentation, GitBranch, ArrowRight, Store,
} from "lucide-react";
import { scrollToForm, trackEvent } from "@/lib/tracking";

const ACCENT = "#BEF264";

const CARDS = [
  { Icon: Building2, title: "Business websites", copy: "Professional websites that present your services and guide visitors to enquire.", action: "Submit an enquiry" },
  { Icon: Target, title: "Campaign landing pages", copy: "Focused pages for Google, Meta, email and other campaigns, built around one offer.", action: "Respond to one offer" },
  { Icon: Calculator, title: "Quotation-request journeys", copy: "Structured quote forms that collect the information your team needs to respond.", action: "Request a quotation" },
  { Icon: CalendarCheck, title: "Consultation booking pages", copy: "Booking journeys with confirmations, reminders and follow-up.", action: "Book a consultation" },
  { Icon: FileDown, title: "Lead-magnet landing pages", copy: "Guide or resource downloads that begin a relevant follow-up journey.", action: "Download a resource" },
  { Icon: Presentation, title: "Webinar & event registration", copy: "Registration pages with reminders before and follow-up after the event.", action: "Register to attend" },
  { Icon: GitBranch, title: "Multi-step sales funnels", copy: "Journeys that connect lead capture, qualification, booking and follow-up.", action: "Move to the next step" },
];

export default function SalesWhatWeCreate() {
  const handleCta = () => {
    trackEvent("cta_click", { location: "sales_solutions" });
    scrollToForm();
  };

  return (
    <section id="solutions" aria-labelledby="solutions-heading" data-testid="web-builds-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-600 font-semibold mb-6">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#0B1120" }} /> Business website design
          </div>
          <h2 id="solutions-heading" className="font-display text-4xl sm:text-5xl lg:text-[56px] font-black tracking-[-0.03em] leading-[1.03] text-zinc-900">
            Websites and Customer Journeys Built Around Your Sales Process
          </h2>
          <p className="mt-6 text-zinc-600 leading-relaxed text-base sm:text-lg">
            Each page Rekisa creates is designed around a specific customer action — and connected to what should happen after it.
          </p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map(({ Icon, title, copy, action }, i) => (
            <div key={title} data-testid={`sales-solution-${i}`}
              className="group rounded-2xl border border-zinc-200 bg-white p-5 transition-[border-color,box-shadow,transform] duration-300 hover:border-zinc-300 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 flex flex-col">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-zinc-900">
                <Icon className="h-5 w-5" style={{ color: ACCENT }} />
              </div>
              <h3 className="mt-4 font-display text-lg font-black text-zinc-900">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-snug text-zinc-500 flex-1">{copy}</p>
              <div className="mt-4 inline-flex self-start items-center gap-1.5 rounded-full bg-zinc-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} />
                Customer action: {action}
              </div>
            </div>
          ))}
          <div data-testid="sales-solution-custom" className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 p-5 flex flex-col">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-white border border-zinc-200">
              <Store className="h-5 w-5 text-zinc-500" />
            </div>
            <h3 className="mt-4 font-display text-lg font-black text-zinc-900">Additional custom services</h3>
            <p className="mt-1.5 text-[13px] leading-snug text-zinc-500">
              Online stores and more advanced digital applications can be scoped separately as custom projects.
            </p>
          </div>
        </div>

        <div className="mt-12">
          <button
            onClick={handleCta}
            data-testid="sales-what-we-create-cta"
            className="inline-flex items-center gap-1.5 rounded-full min-h-12 px-7 py-3 text-base font-bold text-white bg-[#0B1120] hover:brightness-125 transition-[filter]"
          >
            Get My Website &amp; Sales System Plan <ArrowRight className="h-5 w-5" style={{ color: ACCENT }} />
          </button>
        </div>
      </div>
    </section>
  );
}
